import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiUsers,
  FiGlobe,
  FiLock,
  FiMapPin,
  FiCalendar,
  FiBook,
  FiActivity,
  FiEye,
  FiMessageCircle,
  FiMessageSquare,
  FiStar,
  FiUserPlus,
  FiAward,
  FiShield,
  FiTrendingUp,
  FiBarChart2,
} from "react-icons/fi";
import { toast } from "react-toastify";

// Services
import communityService from "../../../services/communityService";

// Sub-components
import CommunityStats from "../CommunityStats";
import CommunityTimeline from "../CommunityTimeline";
import CommunityMembers from "../CommunityMembers";
import CommunityInsights from "../CommunityInsights";
import CommunityThreads from "../threads/CommunityThreads";
import CommunityChat from "../CommunityChat";
import CommunityRooms from "../CommunityRooms";
import AnalyticsDashboard from "../analytics/AnalyticsDashboard";
import JoinRequestModal from "../JoinRequestModal";
import CommunityRatingModal from "../CommunityRatingModal";

// Styles
import "./cpv-main.css";

// ============================================
// ANIMATION VARIANTS
// ============================================

const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.08,
    },
  },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const tabContentVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, x: 10, transition: { duration: 0.15 } },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatDate = (dateString) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

const formatCount = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count?.toString() || "0";
};

// ============================================
// SUB-COMPONENTS
// ============================================

// Type Badge
const TypeBadge = ({ type }) => (
  <span className={`cpv-type-badge ${type}`}>
    {type === "public" ? (
      <>
        <FiGlobe size={10} />
        Public
      </>
    ) : (
      <>
        <FiLock size={10} />
        Private
      </>
    )}
  </span>
);

// Role Badge
const RoleBadge = ({ role }) => {
  const roleConfig = {
    creator: { icon: FiAward, label: "Creator" },
    admin: { icon: FiShield, label: "Admin" },
    moderator: { icon: FiShield, label: "Mod" },
    member: { icon: FiUsers, label: "Member" },
  };

  const config = roleConfig[role];
  if (!config) return null;

  const Icon = config.icon;
  return (
    <span className={`cpv-role-badge ${role}`}>
      <Icon size={10} />
      {config.label}
    </span>
  );
};

// Stat Pill
const StatPill = ({ icon: Icon, value, label }) => (
  <div className="cpv-stat-pill">
    <Icon size={12} />
    <span>
      {value} {label}
    </span>
  </div>
);

// Read-Only Banner
const ReadOnlyBanner = ({ message, onJoin }) => (
  <motion.div
    className="cpv-banner"
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="cpv-banner-content">
      <FiEye size={16} />
      <span className="cpv-banner-text">{message} - Join for full access</span>
    </div>
    <button className="cpv-banner-btn" onClick={onJoin}>
      <FiUserPlus size={12} />
      Join
    </button>
  </motion.div>
);

// Sidebar Content Component
const SidebarContent = ({
  communityId,
  previewData,
  community,
  userMembership,
}) => (
  <>
    <CommunityMembers
      communityId={communityId}
      preview={true}
      previewData={previewData}
      community={community}
      userMembership={userMembership}
    />
    <CommunityInsights
      community={community}
      analytics={previewData?.analytics}
      trendingTopics={previewData?.trendingTopics}
      previewData={previewData}
    />
  </>
);

// ============================================
// MAIN COMPONENT
// ============================================

const CommunityPreviewNew = ({ communityId: propCommunityId }) => {
  // Accept communityId as prop (from PersistentCommunitiesLayout) or fallback to useParams
  const { communityId: paramCommunityId } = useParams();
  const communityId = propCommunityId || paramCommunityId;
  const navigate = useNavigate();

  // State
  const [community, setCommunity] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);

  // Fetch community preview data
  const fetchPreviewData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await communityService.getCommunityPreview(communityId);

      if (response.success) {
        setCommunity(response.data.community);
        setPreviewData(response.data);
      } else {
        setError(response.message || "Failed to load community");
      }
    } catch (err) {
setError(err.message || "Failed to load community");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    fetchPreviewData();
  }, [fetchPreviewData]);

  // Handlers
  const handleJoinRequest = useCallback(() => {
    setShowJoinModal(true);
  }, []);

  const handleJoinRequestSuccess = useCallback(() => {
    setShowJoinModal(false);
    fetchPreviewData();
    toast.success("Your request has been submitted!");
  }, [fetchPreviewData]);

  const handleRatingSubmitted = useCallback(() => {
    setShowRatingModal(false);
    fetchPreviewData();
    toast.success("Rating submitted successfully!");
  }, [fetchPreviewData]);

  // Memoized values
  const userMembership = useMemo(
    () => previewData?.userMembership || community?.userMembership,
    [previewData, community]
  );

  const communityImageUrl = useMemo(
    () => community?.signedPhotoURL || community?.photoURL,
    [community]
  );

  // Get available tabs
  const tabs = useMemo(() => {
    if (!community) return [];

    const baseTabs = [{ id: "overview", label: "Overview", icon: FiEye }];
    const settings = community?.settings;

    if (settings?.allowPublicDiscussionViewing) {
      baseTabs.push({
        id: "discussions",
        label: "Discussions",
        icon: FiMessageCircle,
      });
    }

    if (settings?.allowPublicChatViewing) {
      baseTabs.push({ id: "chat", label: "Chat", icon: FiMessageSquare });
    }

    if (settings?.allowPublicMemberViewing) {
      baseTabs.push({ id: "members", label: "Members", icon: FiUsers });
    }

    if (settings?.allowPublicRoomViewing) {
      baseTabs.push({ id: "rooms", label: "Rooms", icon: FiBook });
    }

    baseTabs.push(
      { id: "activity", label: "Activity", icon: FiActivity },
      { id: "insights", label: "Insights", icon: FiBarChart2 }
    );

    if (userMembership?.role) {
      baseTabs.push({
        id: "analytics",
        label: "Analytics",
        icon: FiTrendingUp,
      });
    }

    return baseTabs;
  }, [community, userMembership]);

  // Loading state
  if (loading) {
    return (
      <div className="cpv-page">
        <div className="cpv-container">
          <div className="cpv-loading">
            <div className="cpv-spinner" />
            <p className="cpv-loading-text">Loading community...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !community) {
    return (
      <div className="cpv-page">
        <div className="cpv-container">
          <div className="cpv-error">
            <h2 className="cpv-error-title">Community Not Found</h2>
            <p className="cpv-error-message">
              {error || "The requested community could not be found."}
            </p>
            <motion.button
              className="cpv-btn cpv-btn-primary"
              onClick={() => navigate(-1)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiArrowLeft size={16} />
              <span>Back</span>
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="cpv-page"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="cpv-container">
        {/* Compact Header */}
        <motion.header className="cpv-header" variants={itemVariants}>
          <div className="cpv-header-row">
            {/* Back Button */}
            <motion.button
              className="cpv-back-btn"
              onClick={() => navigate(-1)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Go Back"
            >
              <FiArrowLeft size={18} />
            </motion.button>

            {/* Avatar */}
            <div className="cpv-avatar">
              {communityImageUrl ? (
                <img src={communityImageUrl} alt={community.name} />
              ) : (
                <div className="cpv-avatar-placeholder">
                  <FiUsers size={24} />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="cpv-info">
              <div className="cpv-title-row">
                <h1 className="cpv-name">{community.name}</h1>
                <TypeBadge type={community.type} />
                {userMembership?.role && (
                  <RoleBadge role={userMembership.role} />
                )}
              </div>

              <p className="cpv-description">{community.description}</p>

              <div className="cpv-meta-stats-row">
                <div className="cpv-meta">
                  <div className="cpv-meta-item">
                    <FiMapPin size={12} />
                    <span>{community.field}</span>
                  </div>
                  <div className="cpv-meta-item">
                    <FiCalendar size={12} />
                    <span>{formatDate(community.createdAt)}</span>
                  </div>
                </div>

                <div className="cpv-stats-inline">
                  <StatPill
                    icon={FiUsers}
                    value={formatCount(community.memberCount)}
                    label="members"
                  />
                  <StatPill
                    icon={FiBook}
                    value={formatCount(community.roomCount || 0)}
                    label="rooms"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="cpv-actions">
              {userMembership?.role ? (
                <>
                  <motion.button
                    className="cpv-btn cpv-btn-primary"
                    onClick={() => navigate(`/communities/${communityId}`)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiMessageSquare size={14} />
                    <span>Enter</span>
                  </motion.button>
                  <motion.button
                    className="cpv-btn cpv-btn-secondary"
                    onClick={() => setShowRatingModal(true)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiStar size={14} />
                  </motion.button>
                </>
              ) : (
                <motion.button
                  className="cpv-btn cpv-btn-primary"
                  onClick={handleJoinRequest}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiUserPlus size={14} />
                  <span>Join</span>
                </motion.button>
              )}
            </div>
          </div>
        </motion.header>

        {/* Navigation Tabs */}
        <motion.nav className="cpv-nav" variants={itemVariants}>
          <div className="cpv-tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <motion.button
                  key={tab.id}
                  className={`cpv-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.nav>

        {/* Read-only Banner */}
        {!userMembership?.role &&
          (activeTab === "discussions" ||
            activeTab === "members" ||
            activeTab === "rooms") && (
            <ReadOnlyBanner
              message={`Viewing ${activeTab} in preview mode`}
              onJoin={handleJoinRequest}
            />
          )}

        {/* Content */}
        <motion.section className="cpv-content" variants={itemVariants}>
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <motion.div
                key="overview"
                className="cpv-grid"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="cpv-main">
                  <CommunityStats
                    community={community}
                    analytics={previewData?.analytics}
                    previewData={previewData}
                  />
                  <CommunityTimeline
                    communityId={communityId}
                    activities={previewData?.recentActivity}
                    analytics={previewData?.analytics}
                  />
                </div>
                <aside className="cpv-sidebar cpv-sidebar-desktop">
                  <SidebarContent
                    communityId={communityId}
                    previewData={previewData}
                    community={community}
                    userMembership={userMembership}
                  />
                </aside>
              </motion.div>
            )}

            {/* Discussions Tab */}
            {activeTab === "discussions" && (
              <motion.div
                key="discussions"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <CommunityThreads
                  communityId={communityId}
                  community={community}
                  isReadOnly={true}
                  userMembership={userMembership}
                />
              </motion.div>
            )}

            {/* Chat Tab */}
            {activeTab === "chat" && (
              <motion.div
                key="chat"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <CommunityChat
                  communityId={communityId}
                  community={community}
                  userRole={userMembership?.role}
                  userMembership={userMembership}
                  isReadOnly={true}
                />
              </motion.div>
            )}

            {/* Members Tab */}
            {activeTab === "members" && (
              <motion.div
                key="members"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <CommunityMembers
                  communityId={communityId}
                  preview={false}
                  previewData={previewData}
                  community={community}
                  userMembership={userMembership}
                />
              </motion.div>
            )}

            {/* Rooms Tab */}
            {activeTab === "rooms" && (
              <motion.div
                key="rooms"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <CommunityRooms
                  communityId={communityId}
                  rooms={previewData?.rooms}
                  community={previewData?.community || community}
                  userRole={userMembership?.role}
                  currentUser={userMembership?.user}
                />
              </motion.div>
            )}

            {/* Activity Tab */}
            {activeTab === "activity" && (
              <motion.div
                key="activity"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <CommunityTimeline
                  communityId={communityId}
                  detailed={true}
                  activities={previewData?.recentActivity}
                  analytics={previewData?.analytics}
                />
              </motion.div>
            )}

            {/* Insights Tab */}
            {activeTab === "insights" && (
              <motion.div
                key="insights"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <CommunityInsights
                  community={community}
                  detailed={true}
                  analytics={previewData?.analytics}
                  trendingTopics={previewData?.trendingTopics}
                  previewData={previewData}
                />
              </motion.div>
            )}

            {/* Analytics Tab */}
            {activeTab === "analytics" && (
              <motion.div
                key="analytics"
                className="cpv-full-width"
                variants={tabContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <AnalyticsDashboard communityId={communityId} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showJoinModal && (
          <JoinRequestModal
            isOpen={showJoinModal}
            onClose={() => setShowJoinModal(false)}
            community={community}
            onSuccess={handleJoinRequestSuccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRatingModal && (
          <CommunityRatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            communityId={communityId}
            communityName={community?.name}
            onRatingSubmitted={handleRatingSubmitted}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CommunityPreviewNew;
